import React, { useEffect, useState } from "react";
import QuestionnaireButton from "@/pages/deals/components/DealStages/components/QuestionnaireButton";
import Text from "@/components/ui/text";
import { find, map } from "lodash";
import { DealsQuestionnaireData, DealStage } from "@/pages/deals/types";
import { ScoreType } from "@/pages/deals/scoreConfig";
import IconContainer from "@/components/ui/icon-container";
import { ChevronDown } from "@dreamhub/designsystem/icons";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import Collapse from "@/components/ui/collapse";
import { cn } from "@/utils/utils";
import WorkflowMenuItemIcon from "@/pages/deals/components/DealStages/components/WorkflowMenuItemIcon";

interface Props {
  stage: DealStage;
  isCurrentStage: boolean;
  isPreviousStage: boolean;
  hasSelectedQuestionnaire: boolean;
  stageQuestionnaires: DealsQuestionnaireData[];
  selectedQuestionnaireId: string | undefined;
  onQuestionnaireSelect: (questionnaireId: string) => void;
  onStageSelect?: (stageId: number) => void;
  selectedStageId?: number | null;
  isLoadingScore: boolean;
}

const WorkflowsMenuItem: React.FC<Props> = ({
  stage,
  isCurrentStage,
  isPreviousStage,
  hasSelectedQuestionnaire,
  stageQuestionnaires,
  selectedQuestionnaireId,
  onQuestionnaireSelect,
  onStageSelect,
  selectedStageId,
  isLoadingScore
}) => {
  const questionnairesWithQuestions = stageQuestionnaires.filter(
    (q) => q.questions && q.questions.length > 0
  );

  const [isOpen, setIsOpen] = useState(hasSelectedQuestionnaire);

  const isEmptyStageSelected =
    questionnairesWithQuestions.length === 0 && selectedStageId === stage.id;

  useEffect(() => {
    if (hasSelectedQuestionnaire) {
      setIsOpen(true);
    }
  }, [hasSelectedQuestionnaire]);

  const pocQuestionnaire = find(
    stageQuestionnaires,
    (questionnaire: DealsQuestionnaireData) =>
      questionnaire.kpiValue === ScoreType.POC
  );

  const handleStageClick = (e: React.MouseEvent) => {
    if (questionnairesWithQuestions.length === 0 && onStageSelect) {
      e.preventDefault();
      e.stopPropagation();
      onStageSelect(stage.id);
    }
  };

  return (
    <Collapse
      open={isOpen}
      onOpenChange={
        questionnairesWithQuestions.length > 0 ? setIsOpen : undefined
      }
      trigger={(isOpen) => (
        <div
          className="flex gap-2 py-1 items-center"
          onClick={handleStageClick}
        >
          <WorkflowMenuItemIcon
            isCurrentStage={isCurrentStage}
            isPreviousStage={isPreviousStage}
          />
          <div
            className={cn(
              "group flex items-center justify-between py-2 pl-3 pr-2 rounded-xl cursor-pointer w-full transition-all duration-200 ease-in-out border-[0.5px] border-transparent",
              isEmptyStageSelected
                ? "bg-surface-elevation1 border-border-border1"
                : "hover:bg-surface-hover-soft"
            )}
          >
            <Text variant="bodySecondary">{stage.name}</Text>
            {questionnairesWithQuestions.length > 0 && (
              <IconContainer
                size="5"
                color="secondary"
                className={cn(
                  "transition-all duration-200 ease-in-out opacity-0 group-hover:opacity-100",
                  isOpen ? "rotate-180" : ""
                )}
              >
                <ChevronDown />
              </IconContainer>
            )}
          </div>
        </div>
      )}
    >
      <div className="flex flex-col gap-2 ml-8 relative">
        <div
          className="absolute -left-6 inset-0 w-1 rounded-lg bg-border-field-disabled"
          style={{
            background:
              isCurrentStage || isPreviousStage
                ? getColorForMilestoneByType(stage.milestone, "border")
                : undefined
          }}
        />
        {map(questionnairesWithQuestions, (questionnaire, index) => {
          const isSelected = questionnaire.id === selectedQuestionnaireId;

          return (
            <QuestionnaireButton
              key={questionnaire.name + index}
              questionnaire={questionnaire}
              isSelected={isSelected}
              isCurrentStage={isCurrentStage}
              onClick={() => onQuestionnaireSelect(questionnaire.id)}
              isLoadingScore={isLoadingScore}
              pocQuestionnaireName={pocQuestionnaire?.name}
            />
          );
        })}
      </div>
    </Collapse>
  );
};

export default WorkflowsMenuItem;
